/**
 * The designer's chrome.
 *
 * Sticky, on the app ground, with no divider under it — the 2026-09-09 app
 * chrome decision. It used to be translucent and blurred over `--bg-page`; it
 * is opaque `--app-ground` now, because the thing scrolling underneath it is a
 * panel with its own edge and blurring a panel's rounded corner through the bar
 * reads as a smear rather than as glass. The bar also carries the top gutter as
 * its own padding, so it can stick to the top of the window without leaving a
 * transparent band for the panel to scroll through.
 *
 * The mockup drew three nav items: Submissions, Templates, Settings. Only
 * Submissions is built. Templates does not exist yet (docs/BACKLOG.md), and
 * settings are per-project rather than global, so they live on the submission
 * itself. A nav item that opens nothing is worse than one that is absent.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from '../router';
import { Wordmark } from './Wordmark';
import { Bell } from './Bell';
import { PresenceDot, usePresence } from './Presence';
import { ds } from '../ds';
import { LAYOUT, RADIUS, initials, mono, monoSmall } from '../theme';
import { useApp } from '../state';
import type { Me } from '../state';
import { PRESENCE_LABEL, PRESENCE_STATES, isSettablePresence } from '@shared/presence';
import { setPresence } from '@shared/presence-client';

/**
 * The chip's background, flattened.
 *
 * The dot cuts itself out of whatever it sits on, and a translucent ring shows
 * the dot's own edge through it. `--accent-soft` on the dark surface is
 * `rgba(203,176,209,.16)`; the bar under it is now the opaque app ground
 * (`--app-ground`, #12142e), so this is that blend — re-derive it if either
 * changes.
 */
const CHIP_ON_NAV = '#302d48';

/** The four somebody may choose. `offline` and `unknown` are observations. */
const CHOICES = PRESENCE_STATES.filter(isSettablePresence);

export function TopNav() {
  const { me } = useApp();

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        // The gutter above the bar is the bar's own padding — see the note at
        // the top of this file.
        height: LAYOUT.topbar + LAYOUT.gutter,
        flex: 'none',
        paddingTop: LAYOUT.gutter,
        // Opaque, because it is sticky and the content panel scrolls under it.
        background: 'var(--app-ground)'
      }}
    >
      <div
        style={{
          maxWidth: LAYOUT.page,
          height: '100%',
          margin: '0 auto',
          padding: '0 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <Wordmark size={15} reversed />
          </Link>
          <Link to="/" style={{ fontSize: 13, color: 'var(--text-strong)', fontWeight: 500 }}>
            Submissions
          </Link>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Bell />
          {me && <StatusChip me={me} />}
        </div>
      </div>
    </div>
  );
}

/**
 * The signed-in designer's chip, and the status behind it.
 *
 * The only place in Scope that draws a presence dot. The other people this app
 * shows — the expert on a submission, the actor on a notification — have no
 * Mauvely account, and there is nothing to draw.
 *
 * Dismissal is the contract every popover in the suite uses: outside mousedown,
 * and Escape. The same as Bell, beside it.
 */
function StatusChip({ me }: { me: Me }) {
  const { Icon } = ds();
  const { presence, override } = usePresence();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (anchor.current && !anchor.current.contains(e.target as Node)) setOpen(false);
    };
    const onEscape = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const choose = (next: (typeof CHOICES)[number] | null) => {
    setOpen(false);
    void setPresence(next);
  };

  return (
    <div ref={anchor} style={{ position: 'relative' }}>
      <button
        type="button"
        title={me.email}
        aria-label="Status"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          ...mono,
          // The dot is `position: absolute`, and the chip is what it is
          // absolute inside.
          position: 'relative',
          width: 30,
          height: 30,
          padding: 0,
          border: 0,
          borderRadius: '50%',
          background: 'var(--accent-soft)',
          color: 'var(--accent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          letterSpacing: '.04em',
          cursor: 'pointer'
        }}
      >
        {initials(me.name, me.email)}
        <PresenceDot presence={presence} size={9} ring={CHIP_ON_NAV} />
      </button>

      {open && (
        <div
          className="mv-pop"
          style={{
            position: 'absolute',
            top: 40,
            right: 0,
            width: 'min(340px, calc(100vw - 32px))',
            background: 'var(--surface-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: RADIUS.card,
            boxShadow: '0 18px 44px rgba(8,6,16,.4)',
            overflow: 'hidden',
            zIndex: 6
          }}
        >
          <div
            style={{
              ...monoSmall,
              padding: '14px 16px 10px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <span>Status</span>
            {override && (
              <button
                type="button"
                onClick={() => choose(null)}
                style={{
                  ...mono,
                  fontSize: 9,
                  border: 0,
                  background: 'transparent',
                  color: 'var(--primary-text)'
                }}
              >
                Clear
              </button>
            )}
          </div>

          {CHOICES.map((choice) => {
            const chosen = override === choice;
            return (
              <button
                key={choice}
                type="button"
                className="mv-row"
                // Choosing what is already chosen clears it — the same gesture
                // as Clear above, for somebody who never looked up there.
                onClick={() => choose(chosen ? null : choice)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 11,
                  width: '100%',
                  textAlign: 'left',
                  border: 0,
                  background: 'transparent',
                  color: 'var(--text-body)',
                  padding: '12px 16px',
                  borderTop: '1px solid var(--border-subtle)',
                  cursor: 'pointer'
                }}
              >
                <span
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    width: 10,
                    height: 10,
                    flex: 'none'
                  }}
                >
                  <PresenceDot presence={choice} size={10} ring="var(--surface-card)" />
                </span>
                <span style={{ flex: 1, fontSize: 13.5 }}>{PRESENCE_LABEL[choice]}</span>
                {chosen && <Icon name="check" size={14} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
